import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Megaphone, Send } from 'lucide-react';
import type { UnifiedMessage } from '@/types/unified-messaging';
import type { ClubChannelMessage } from '@/types/club-community';
import { clubCommunityService } from '@/services/api/club-community.service';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useAppTranslations } from '@/hooks/useAppTranslations';
import { UnifiedMessageBubble } from './UnifiedMessageBubble';
import { useModalBodyFlag } from '@/hooks/useModalBodyFlag';

interface AnnouncementThreadSheetProps {
  isOpen: boolean;
  onClose: () => void;
  announcement: UnifiedMessage;
  channelId: string;
  channelName?: string;
}

export function AnnouncementThreadSheet({
  isOpen,
  onClose,
  announcement,
  channelId,
}: AnnouncementThreadSheetProps) {
  const { user } = useAuth();
  const { t, language } = useAppTranslations();
  const [replies, setReplies] = useState<ClubChannelMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const repliesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useModalBodyFlag(isOpen);

  // Fetch replies when sheet opens
  useEffect(() => {
    if (!isOpen) {
      setReplies([]);
      setInputText('');
      return;
    }
    setLoading(true);
    clubCommunityService
      .getThreadReplies(announcement.id, channelId)
      .then(result => { if (result.success) setReplies(result.data ?? []); })
      .finally(() => setLoading(false));
  }, [isOpen, announcement.id, channelId]);

  // Real-time subscription for new thread replies.
  // Uses channelRef to tear down any in-flight subscription before creating a new one,
  // preventing duplicate channels when the sheet is opened/closed rapidly.
  useEffect(() => {
    if (!isOpen) return;
    // Tear down any existing subscription first
    if (channelRef.current) { supabase.removeChannel(channelRef.current); }
    const channel = supabase
      .channel(`thread-${announcement.id}`)
      .on(
        'postgres_changes' as any,
        {
          event: 'INSERT',
          schema: 'public',
          table: 'club_channel_messages',
          filter: `reply_to_id=eq.${announcement.id}`,
        },
        async (payload: any) => {
          // Skip own messages added optimistically. Require user?.id to be truthy
          // so we never match undefined === undefined if auth is momentarily null.
          if (user?.id && payload.new?.sender_id === user.id) return;
          const { data: msg } = await supabase
            .from('club_channel_messages')
            .select('*, sender:profiles!sender_id(id, display_name, avatar_url, handicap)')
            .eq('id', payload.new.id)
            .single();
          if (msg) {
            setReplies(prev =>
              prev.some(r => r.id === (msg as any).id)
                ? prev
                : [...prev, { ...(msg as any), reactions: [], reaction_counts: {}, user_has_reacted: {}, mentions: [] }]
            );
          }
        }
      )
      .subscribe();
    channelRef.current = channel;
    return () => {
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [isOpen, announcement.id, user?.id]);

  // Auto-scroll to bottom
  useEffect(() => {
    repliesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [replies.length]);

  const handleSend = useCallback(async () => {
    if (!inputText.trim() || sending) return;
    const content = inputText.trim();
    setInputText('');
    setSending(true);

    const tempId = `temp-${Date.now()}`;
    const optimisticReply: ClubChannelMessage = {
      id: tempId,
      channel_id: channelId,
      sender_id: user?.id ?? '',
      content,
      message_type: 'text',
      reply_to_id: announcement.id,
      thread_root_id: announcement.id,
      is_pinned: false,
      created_at: new Date().toISOString(),
      sender: { id: user?.id ?? '', display_name: 'You', avatar_url: undefined as any },
      reactions: [],
      reaction_counts: {},
      user_has_reacted: {},
      mentions: []
    } as any;
    setReplies(prev => [...prev, optimisticReply]);

    try {
      const result = await clubCommunityService.sendMessage({
        channel_id: channelId,
        content,
        message_type: 'text',
        reply_to_id: announcement.id,
        thread_root_id: announcement.id,
      });
      if (result.success && result.data) {
        setReplies(prev =>
          prev.map(r =>
            r.id === tempId
              ? { ...result.data!, reactions: [], reaction_counts: {}, user_has_reacted: {}, mentions: [] }
              : r
          )
        );
      }
    } catch {
      setReplies(prev => prev.filter(r => r.id !== tempId));
      setInputText(content);
    } finally {
      setSending(false);
    }
  }, [inputText, sending, channelId, announcement.id, user?.id]);

  if (!isOpen) return null;

  const title = (announcement.message_data as any)?.title as string | undefined;

  const adaptReplyToUnified = (reply: ClubChannelMessage): UnifiedMessage => ({
    id: reply.id,
    conversation_id: channelId,
    channel_id: channelId,
    sender_id: reply.sender_id,
    content: reply.content ?? '',
    message_type: (reply.message_type as any) ?? 'text',
    message_data: reply.message_data as any,
    created_at: reply.created_at,
    updated_at: (reply as any).edited_at ?? reply.created_at,
    edited_at: (reply as any).edited_at,
    reply_to_id: null as any,
    reply_to: null as any,
    reaction_counts: reply.reaction_counts ?? {},
    user_has_reacted: reply.user_has_reacted ?? {},
    sender: reply.sender ?? null as any,
    message_context: 'channel' as const,
  });

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/40 z-[69]"
        style={{ backdropFilter: 'blur(4px)' }}
        onClick={onClose}
      />
      {/* Sheet */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[70] flex flex-col animate-slide-up"
        style={{
          maxHeight: '90vh',
          background: 'white',
          borderTopLeftRadius: 24,
          borderTopRightRadius: 24,
          boxShadow: '0 -8px 32px rgba(0,0,0,0.18)',
        }}
      >
        {/* Drag handle + close */}
        <div className="flex justify-center pt-3 pb-2 flex-shrink-0 relative">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
          <button
            onClick={onClose}
            className="absolute right-4 top-3 w-7 h-7 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.06)' }}
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        {/* Announcement summary header */}
        <div className="px-4 pb-3 flex-shrink-0">
          <div className="flex items-start gap-2.5 mb-2">
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
              style={{ background: 'rgba(220,38,38,0.1)' }}
            >
              <Megaphone className="w-3.5 h-3.5" style={{ color: '#dc2626' }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                {t.clubCommunity?.clubAnnouncement ?? 'Club Announcement'}
              </p>
              {title && (
                <p className="text-[14px] font-bold text-gray-900 leading-snug mt-0.5">{title}</p>
              )}
              <p className="text-[13px] text-gray-500 leading-relaxed mt-0.5 line-clamp-2">
                {announcement.content}
              </p>
            </div>
          </div>
          <div className="h-px bg-gray-100" />
        </div>

        {/* Reply list */}
        <div className="flex-1 overflow-y-auto px-4 min-h-0">
          {loading ? (
            <div className="space-y-3 py-3">
              {[0, 1, 2].map(i => (
                <div key={i} className="flex items-end gap-2">
                  <div className="w-7 h-7 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
                  <div
                    className="rounded-2xl bg-gray-100 animate-pulse"
                    style={{ width: `${50 + i * 12}%`, height: 36 }}
                  />
                </div>
              ))}
            </div>
          ) : replies.length === 0 ? (
            <div className="flex items-center justify-center py-12">
              <p className="text-[13px] text-gray-400 text-center">
                {t.clubCommunity?.threadNoReplies ?? 'No replies yet. Be the first!'}
              </p>
            </div>
          ) : (
            <div className="py-2 space-y-1">
              {replies.map((reply, idx) => {
                const isOwn = reply.sender_id === user?.id;
                const unified = adaptReplyToUnified(reply);
                const prevReply = replies[idx - 1];
                const isFirst = !prevReply || prevReply.sender_id !== reply.sender_id;
                const nextReply = replies[idx + 1];
                const isLast = !nextReply || nextReply.sender_id !== reply.sender_id;
                return (
                  <UnifiedMessageBubble
                    key={reply.id}
                    message={unified}
                    isOwn={isOwn}
                    isFirstInGroup={isFirst}
                    isLastInGroup={isLast}
                    isMiddleInGroup={!isFirst && !isLast}
                    isEditing={false}
                    editContent=""
                    onEditChange={() => {}}
                    onSaveEdit={() => {}}
                    onCancelEdit={() => {}}
                    showReactionPicker={false}
                    longPressedMessage={null}
                    reactingMessages={new Set()}
                    onReaction={() => {}}
                    onEdit={() => {}}
                    onDelete={() => {}}
                    onReply={() => {}}
                    onLongPressStart={() => {}}
                    onLongPressEnd={() => {}}
                    onMessageTap={() => {}}
                    onDismissContextMenu={() => {}}
                    showTimestamp={false}
                    allMessages={replies.map(adaptReplyToUnified)}
                    language={language}
                  />
                );
              })}
              <div ref={repliesEndRef} />
            </div>
          )}
        </div>

        {/* Input footer */}
        <div
          className="flex-shrink-0 px-4 pt-2"
          style={{
            paddingBottom: 'max(16px, env(safe-area-inset-bottom, 0px))',
            borderTop: '1px solid rgba(0,0,0,0.06)',
          }}
        >
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              placeholder={t.clubCommunity?.threadReplyPlaceholder ?? 'Reply to announcement…'}
              rows={1}
              className="flex-1 resize-none rounded-2xl text-sm px-3.5 py-2.5 outline-none"
              style={{
                background: 'rgba(0,0,0,0.05)',
                maxHeight: 100,
                lineHeight: '1.4',
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <button
              onClick={handleSend}
              disabled={!inputText.trim() || sending}
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 transition-all active:scale-95 disabled:opacity-40"
              style={{ background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)' }}
            >
              <Send className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
